import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@bri/shared-components';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { cn } from '@/lib/utils';
import { formatWorkspaceRole } from '@/lib/workspaceRoles';
import {
  HomeIcon,
  FolderIcon,
  UsersIcon,
  CreditCardIcon,
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
  Bars3Icon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

const primaryNavigation = [
  { name: 'Manage Workspaces', href: '/app/workspaces', icon: BuildingOfficeIcon },
  { name: 'Billing', href: '/app/billing', icon: CreditCardIcon },
];

const workspaceNavigation = [
  { name: 'Dashboard', href: '/app', icon: HomeIcon },
  { name: 'Projects', href: '/app/projects', icon: FolderIcon },
  { name: 'Members', href: '/app/team', icon: UsersIcon },
];

function isNavItemActive(pathname: string, href: string) {
  if (href === '/app') {
    return pathname === '/app';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

interface AppSidebarProps {
  className?: string;
}

export function AppSidebar({ className }: AppSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <>
      {/* Mobile sidebar overlay */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <SidebarContent />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar - now inline, not fixed */}
      <aside className={cn("hidden lg:flex lg:w-64 lg:flex-col lg:shrink-0", className)}>
        <SidebarContent />
      </aside>

      {/* Mobile menu button - shown in header area on mobile */}
      <div className="flex items-center gap-x-4 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-gray-700"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </>
  );
}

function SidebarContent() {
  const location = useLocation();
  const { user } = useAuth();
  const { currentWorkspace, workspaces, switchWorkspace, isLoading, isAutoCreating } = useWorkspace();
  const [isWorkspaceListOpen, setIsWorkspaceListOpen] = useState(false);

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    if (workspaceId === currentWorkspace?.workspaceId) {
      setIsWorkspaceListOpen(false);
      return;
    }

    try {
      await switchWorkspace(workspaceId);
      setIsWorkspaceListOpen(false);
    } catch (error) {
      console.error('Failed to switch workspace from sidebar:', error);
    }
  };

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-4 py-4 border-r border-gray-200">
      {/* Loading state for workspace creation */}
      {(isLoading || isAutoCreating) && (
        <div className="pb-4">
          <div className="text-center py-4">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">
              {isAutoCreating ? 'Creating your workspace...' : 'Loading...'}
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-2">
          <li className="pb-2 border-b border-gray-200">
            <div className="px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                Current Workspace
              </p>
              <div className="mt-2 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <span className="text-sm font-semibold">
                      {currentWorkspace?.name?.charAt(0)?.toUpperCase() || 'W'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {currentWorkspace?.name || 'Select a workspace'}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {currentWorkspace ? formatWorkspaceRole(currentWorkspace.role) : 'Choose one from the header'}
                    </p>
                  </div>
                </div>

                {workspaces.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setIsWorkspaceListOpen((open) => !open)}
                    className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  >
                    Switch
                    <ChevronDownIcon
                      className={cn('ml-1 h-4 w-4 transition-transform', isWorkspaceListOpen && 'rotate-180')}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>

              {isWorkspaceListOpen && workspaces.length > 1 && (
                <div className="mt-3 space-y-1">
                  {workspaces.map((workspace) => {
                    const isCurrent = workspace.workspaceId === currentWorkspace?.workspaceId;

                    return (
                      <button
                        key={workspace.workspaceId}
                        type="button"
                        onClick={() => handleWorkspaceSwitch(workspace.workspaceId)}
                        className={cn(
                          'flex w-full items-center gap-x-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                          isCurrent
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                          {workspace.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{workspace.name}</p>
                          <p className="truncate text-xs text-gray-500">{formatWorkspaceRole(workspace.role)}</p>
                        </div>
                        {isCurrent && <CheckIcon className="h-4 w-4 text-primary" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <ul role="list" className="mt-3 space-y-1">
                {workspaceNavigation.map((item) => {
                  const isActive = isNavItemActive(location.pathname, item.href);

                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          'group ml-3 flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        )}
                      >
                        <span
                          className={cn(
                            'h-px w-3 shrink-0 rounded-full',
                            isActive ? 'bg-primary-foreground/70' : 'bg-gray-300'
                          )}
                          aria-hidden="true"
                        />
                        <item.icon
                          className={cn(
                            'h-4 w-4 shrink-0',
                            isActive ? 'text-primary-foreground' : 'text-gray-500 group-hover:text-gray-700'
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>

          <li className="pt-2">
            <ul role="list" className="space-y-1">
              {primaryNavigation.map((item) => {
                const isActive = isNavItemActive(location.pathname, item.href);
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-lg p-3 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isActive ? 'text-primary-foreground' : 'text-gray-500 group-hover:text-gray-700'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
          
          {/* User info at bottom - simplified since profile is in header */}
          <li className="mt-auto pt-4 border-t border-gray-200">
            <div className="flex items-center p-3 rounded-lg">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-medium text-sm">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}
